//////////////////////////////////////////////////////////////////////////////
//
//  URL 빌더 유틸리티
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//
//  설명: API URL 생성 로직 분리
//
//////////////////////////////////////////////////////////////////////////////

import { ApiEndpoint, ApiParams, MapboxParams } from "../types/ApiTypes"

// API Base URL from environment variable
export const getBaseUrl = (): string => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN
  const useProxy = process.env.REACT_APP_USE_PROXY === "true" // 프록시 사용 여부 (선택적)
  const isDev = process.env.NODE_ENV === "development"

  // 개발 모드에서 프록시를 사용하는 경우
  if (isDev && useProxy) {
    console.log("[getBaseUrl] Development mode - using proxy: /v0")
    return "/v0"
  }

  // 서버 CORS 설정을 사용하거나 프로덕션/스테이징 모드에서는 전체 URL 사용
  console.log("[getBaseUrl] REACT_APP_API_DOMAIN:", apiDomain || "not set")
  return apiDomain || "http://localhost:5039/v0"
}

/**
 * 외부 서비스별 URL 생성 핸들러
 */
type UrlHandler = (api: ApiEndpoint, params?: ApiParams) => string

const externalServiceHandlers: Record<string, UrlHandler> = {
  /**
   * Youtube API
   */
  youtube: (api: ApiEndpoint, _params?: ApiParams) => {
    return `${api.baseUrl}${api.url}`
  },
}

/**
 * 외부 서비스 URL 생성
 */
const buildExternalServiceUrl = (api: ApiEndpoint, params?: ApiParams): string => {
  const handler = externalServiceHandlers[api.externalService!]

  if (!handler) {
    console.warn(`Unknown external service: ${api.externalService}, using default URL`)
    return `${api.baseUrl}${api.url}`
  }

  return handler(api, params)
}

/**
 * 내부 API URL 생성
 */
const buildInternalApiUrl = (api: ApiEndpoint, params?: ApiParams & { pathParams?: Record<string, string> }): string => {
  let url = api.url;

  // pathParams 처리 (예: /user/{id} -> /user/123)
  if (api.pathParams && params?.pathParams) {
    api.pathParams.forEach((paramName) => {
      const paramValue = params.pathParams?.[paramName];
      if (paramValue !== undefined) {
        // {paramName} 또는 :paramName 형식으로 치환
        url = url.replace(`{${paramName}}`, paramValue);
        url = url.replace(`:${paramName}`, paramValue);
      }
    });
  }

  return `${getBaseUrl()}${url}`
}

/**
 * API URL 생성 (메인 함수)
 * @param api API 엔드포인트 정의
 * @param params 요청 파라미터
 * @returns 생성된 URL
 */
export const buildApiUrl = (api: ApiEndpoint, params?: ApiParams): string => {
  // 외부 서비스
  if (api.externalService) {
    return buildExternalServiceUrl(api, params)
  }

  // 내부 API
  return buildInternalApiUrl(api, params)
}

/**
 * 외부 서비스 핸들러 등록 (확장 가능)
 */
export const registerExternalServiceHandler = (serviceName: string, handler: UrlHandler) => {
  externalServiceHandlers[serviceName] = handler
}

export default buildApiUrl
